import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Spin, Select } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

export default function AttendanceReport() {
    const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };

    // Get current month & year by default
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const [organisations, setOrganisations] = useState([]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [organisationId, setOrganisationId] = useState(1);
    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: {
                Authorization: `Bearer ${authData?.access}`
            }
        })
        console.log("Org : ", res.data.results)
        setOrganisations(res.data.results)
    }

    useEffect(() => {
        fetchOrganisation()
    }, [])

    // Fetch API Data
    useEffect(() => {
        const fetchAttendance = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${backUrl}/api/reports/attendance-summary?organisation_id=${organisationId}&month=${month}&year=${year}`
                );
                setData(response.data);
            } catch (error) {
                console.error("Error fetching attendance summary:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAttendance();
    }, [organisationId, month, year, backUrl]);

    const COLORS = ["#22C55E", "#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6"];

    const chartData = data
        ? [
            { name: "Present %", value: data.present_percentage },
            { name: "Absent %", value: data.absent_percentage },
            { name: "LOP Days", value: data.lop_days },
            { name: "Overtime Hours", value: data.overtime_hours },
            { name: "Late Days", value: data.late_days },
        ]
        : [];

    // Month dropdown options
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    // Generate year range (current ± 2 years)
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    return (
        <div className="p-6 bg-gray-50 min-h-screen flex flex-col justify-start items-center mb-2">
            {/* Filters */}
            <div className="flex gap-4 mb-6">
                <Select
                    value={organisationId}
                    onChange={(value) => setOrganisationId(value)}
                    style={{ width: 280, marginRight:'2px' }}
                >
                    {organisations.map((item) => (
                        <Option key={item.id} value={item.id}>
                            {item.name}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={month}
                    onChange={(value) => setMonth(value)}
                    style={{ width: 120, marginLeft:'2px' }}
                >
                    {months.map((m, index) => (
                        <Option key={index + 1} value={index + 1}>
                            {m}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={year}
                    onChange={(value) => setYear(value)}
                    style={{ width: 120, marginLeft:'2px' }}
                >
                    {years.map((y) => (
                        <Option key={y} value={y}>
                            {y}
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Chart */}
            {loading ? (
                <Spin size="large" />
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        style={{
                            width: 470,
                            borderRadius: "16px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            textAlign: "center",
                            marginTop:10
                        }}
                    >
                        <h3 style={{ marginBottom: 16 }}>Attendance Summary ({months[month - 1]} {year})</h3>

                        <PieChart width={400} height={400}>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={110}
                                paddingAngle={4}
                                dataKey="value"
                                label
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </Card>
                </motion.div>
            )}
        </div>
    );
}
